//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { darken, lighten } from '@mui/material';
import { HTMLAttributes, MouseEventHandler } from 'react';

type OnOffToggleProps = HTMLAttributes<HTMLDivElement> & {
  label?: string;
  on?: boolean;
  onToggle: (on: boolean) => void;
};

const OnOffToggle = ({ label = '', on = false, onToggle, ...rest }: OnOffToggleProps) => {
  const handleToggle: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    onToggle && onToggle(!on);
  };

  return (
    <Wrapper {...rest} onClick={handleToggle}>
      <Label>{label}</Label>
      <ButtonContainer>
        <ToggleButton type="button" toggled={+on}>
          {on ? 'on' : 'off'}
        </ToggleButton>
      </ButtonContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const Label = styled.span`
  font-size: 0.9rem;
  opacity: 0.66;
  white-space: nowrap;
`;

const ButtonContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 36px;
  height: 16px;
  background: ${({ theme }) => lighten(theme.palette.background.paper, 0.1)};
  border: 1px solid ${({ theme }) => theme.palette.background.default};
  border-radius: 4px;
`;

const ToggleButton = styled.button<{ toggled: number }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border: 1px solid
    ${({ theme }) => (theme.palette.mode === 'light' ? lighten(theme.palette.primary.main, 0.6) : darken(theme.palette.primary.main, 0.5))};
  border-radius: 4px;
  font-weight: 600;
  text-align: center;
  transition: ease transform 300ms;
  transform: translateX(0px);
  font-size: 0.75rem;

  color: ${({ theme, toggled }) => (toggled ? theme.palette.getContrastText(theme.palette.primary.main) : theme.palette.action.disabled)};
  background: ${({ theme, toggled }) => (toggled ? theme.palette.primary.main : theme.palette.background.default)};
  transform: ${({ toggled }) => (toggled ? 'translateX(15px)' : 'translateX(-3px)')};
`;

export default OnOffToggle;
